import type { BillingPricePlanPeriod } from "@/types/billing";

const periodSuffixes: Record<BillingPricePlanPeriod, string> = {
  month: "month",
  quarter: "quarter",
  year: "year",
};

export const formatPlanStorageLimit = (
  storageLimitGb?: string | number | null
): string => {
  if (storageLimitGb === null || storageLimitGb === undefined || storageLimitGb === "") {
    return "Unlimited storage";
  }

  const value = Number(storageLimitGb);
  if (!Number.isFinite(value) || value < 0) {
    return "Unlimited storage";
  }

  const formatted =
    Number.isInteger(value) || value % 1 === 0
      ? value.toLocaleString()
      : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return `${formatted} GB storage`;
};

const formatAmount = (amount: number): string =>
  amount % 1 === 0
    ? amount.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

export const formatMoney = (
  amount?: string | number | null,
  currency?: string | null,
  period?: BillingPricePlanPeriod | string | null
): string | null => {
  if (amount === null || amount === undefined || amount === "") {
    return null;
  }

  const value = Number(amount);
  if (!Number.isFinite(value)) {
    return null;
  }

  const label = `${currency?.trim() || "USD"} ${formatAmount(value)}`;
  if (!period) {
    return label;
  }

  const periodKey = period as BillingPricePlanPeriod;
  const suffix = periodSuffixes[periodKey] ?? period;
  return `${label} / ${suffix}`;
};

export const formatPlanPrice = (
  price?: string | number | null,
  period?: BillingPricePlanPeriod,
  currency?: string | null
): string | null => formatMoney(price, currency ?? "USD", period);

export const formatAddonPriceLabel = (
  price?: string | number | null,
  currency?: string | null,
  period?: BillingPricePlanPeriod | string | null
): string | null => formatMoney(price, currency, period);

export const formatAddonPackLabel = (
  pack: {
    name: string;
    price?: string | number | null;
    currency?: string | null;
    period?: BillingPricePlanPeriod | string | null;
  },
  options?: { includePeriod?: boolean }
): string => {
  const priceLabel = formatAddonPriceLabel(
    pack.price,
    pack.currency,
    options?.includePeriod ? pack.period : null
  );
  return priceLabel ? `${pack.name} — ${priceLabel}` : pack.name;
};

export const parseOptionalDecimal = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed;
};

export const isNonNegativeDecimal = (value: string): boolean => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0;
};
